import React from "react";
import styled from "styled-components";

const TechnologyContainer = styled.div`
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    rgba(0, 191, 255, 0.05) 0%,
    rgba(30, 144, 255, 0.1) 100%
  );

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    padding: 0 16px;
    margin-bottom: 60px;
  }
`;

const TechBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(0, 191, 255, 0.1);
  color: var(--primary-blue);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 2rem;
  border: 1px solid rgba(0, 191, 255, 0.2);
  box-shadow: var(--shadow-light);

  svg {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  .highlight {
    color: var(--primary-blue);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TechCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: var(--shadow-light);
  border: 1px solid var(--light-gray);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const TechIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 2rem;

  &.anion {
    background: var(--gradient-primary);
  }
  &.natural {
    background: var(--gradient-accent);
  }
`;

const TechTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const TechSubtitle = styled.p`
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const TechFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TechFeature = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;

  &::before {
    content: "✓";
    color: var(--accent-green);
    font-weight: bold;
    margin-right: 0.5rem;
    margin-top: 0.1rem;
  }
`;

const TechVisual = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  position: relative;
  border: 1px solid var(--light-gray);
  box-shadow: var(--shadow-light);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const TechVisualIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
  font-size: 3rem;

  &.anion {
    background: var(--gradient-primary);
  }
  &.natural {
    background: var(--gradient-accent);
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 2.5rem;
  }
`;

const TechVisualTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const TechVisualSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const BenefitsSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid var(--light-gray);
  box-shadow: var(--shadow-light);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const BenefitIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
  font-size: 2rem;

  &.protection {
    background: var(--gradient-primary);
  }
  &.comfort {
    background: var(--gradient-accent);
  }
  &.experience {
    background: var(--gradient-cool);
  }
`;

const BenefitTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const DecorativeElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 10%;
    right: 5%;
    width: 60px;
    height: 60px;
    background: rgba(0, 191, 255, 0.1);
    border-radius: 50%;

    @media (max-width: 768px) {
      width: 40px;
      height: 40px;
    }
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 20%;
    left: 10%;
    width: 80px;
    height: 80px;
    background: rgba(30, 144, 255, 0.1);
    border-radius: 50%;

    @media (max-width: 768px) {
      width: 50px;
      height: 50px;
    }
  }
`;

const Technology = () => {
  return (
    <TechnologyContainer>
      <DecorativeElements />
      <Section>
        <div className="text-center">
          <TechBadge>Revolutionary Technology</TechBadge>
          <SectionTitle>
            <span className="highlight">Advanced Innovation</span>
          </SectionTitle>
          <SectionSubtitle>
            Experience the future of feminine hygiene with our cutting-edge
            technology
          </SectionSubtitle>
        </div>

        <TechGrid>
          <TechCard>
            <TechIcon className="anion">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TechIcon>
            <TechTitle>Anion Chip Technology</TechTitle>
            <TechSubtitle>Advanced Ion Generation</TechSubtitle>
            <TechFeatureList>
              <TechFeature>
                Generates negative ions that help neutralize bacteria and odors
              </TechFeature>
              <TechFeature>
                Improves blood circulation and reduces menstrual discomfort
              </TechFeature>
              <TechFeature>
                Maintains pH balance for optimal feminine health
              </TechFeature>
              <TechFeature>
                Provides natural antibacterial protection
              </TechFeature>
            </TechFeatureList>
          </TechCard>

          <TechVisual>
            <TechVisualIcon className="anion">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TechVisualIcon>
            <TechVisualTitle>Anion Chip</TechVisualTitle>
            <TechVisualSubtitle>Advanced Technology</TechVisualSubtitle>
          </TechVisual>
        </TechGrid>

        <TechGrid>
          <TechVisual>
            <TechVisualIcon className="natural">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7.5 15 8.5 14.5 8.5 14.5L17 8Z" fill="white"/>
              </svg>
            </TechVisualIcon>
            <TechVisualTitle>Natural Gelly</TechVisualTitle>
            <TechVisualSubtitle>Aloe Vera & Papaya</TechVisualSubtitle>
          </TechVisual>

          <TechCard>
            <TechIcon className="natural">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7.5 15 8.5 14.5 8.5 14.5L17 8Z" fill="white"/>
              </svg>
            </TechIcon>
            <TechTitle>Aloe Vera Papaya Gelly</TechTitle>
            <TechSubtitle>Natural Healing Power</TechSubtitle>
            <TechFeatureList>
              <TechFeature>
                Aloe Vera provides natural soothing and healing properties
              </TechFeature>
              <TechFeature>
                Papaya extract helps reduce inflammation and irritation
              </TechFeature>
              <TechFeature>
                Natural moisturizing prevents dryness and discomfort
              </TechFeature>
              <TechFeature>
                Gentle on sensitive skin with no harsh chemicals
              </TechFeature>
            </TechFeatureList>
          </TechCard>
        </TechGrid>
      </Section>

      <Section>
        <SectionTitle>Technology Benefits</SectionTitle>
        <SectionSubtitle>
          How our advanced technology improves your experience
        </SectionSubtitle>

        <BenefitsSection>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon className="protection">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.9 16,12.4 16,13V16C16,17.1 15.1,18 14,18H10C8.9,18 8,17.1 8,16V13C8,12.4 8.4,11.9 9,11.5V10C9,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.2,8.7 10.2,10V11.5H13.8V10C13.8,8.7 12.8,8.2 12,8.2Z" fill="white"/>
                </svg>
              </BenefitIcon>
              <BenefitTitle>Enhanced Protection</BenefitTitle>
              <BenefitDescription>
                Advanced absorption and leak-proof technology for maximum
                confidence
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon className="comfort">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z" fill="white"/>
                </svg>
              </BenefitIcon>
              <BenefitTitle>Natural Comfort</BenefitTitle>
              <BenefitDescription>
                Gentle, natural ingredients that care for your skin and overall
                health
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon className="experience">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                </svg>
              </BenefitIcon>
              <BenefitTitle>Superior Experience</BenefitTitle>
              <BenefitDescription>
                Revolutionary design that adapts to your body for ultimate
                comfort
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </BenefitsSection>
      </Section>
    </TechnologyContainer>
  );
};

export default Technology;
